<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { KeyRound, LoaderCircle, LockKeyhole, ShieldCheck, UserRound } from 'lucide-vue-next'

const props = defineProps<{
  pending: boolean
  error: string | null
}>()

const emit = defineEmits<{
  login: [username: string, password: string]
}>()

const { t } = useI18n()
const username = ref('')
const password = ref('')

function submit(): void {
  if (props.pending || !username.value.trim() || !password.value) return
  emit('login', username.value.trim(), password.value)
}
</script>

<template>
  <main class="web-login-shell">
    <section class="web-login-card">
      <div class="web-login-mark" aria-hidden="true">
        <span></span><span></span><ShieldCheck :size="34" />
      </div>
      <span class="modal-eyebrow">{{ t('webLogin.eyebrow') }}</span>
      <h1>{{ t('webLogin.title') }}</h1>
      <p>{{ t('webLogin.description') }}</p>

      <form @submit.prevent="submit">
        <label>
          <span>{{ t('webLogin.account') }}</span>
          <div><UserRound :size="16" /><input v-model.trim="username" autocomplete="username" required autofocus /></div>
        </label>
        <label>
          <span>{{ t('webLogin.password') }}</span>
          <div><KeyRound :size="16" /><input v-model="password" type="password" autocomplete="current-password" required /></div>
        </label>
        <p v-if="error" class="form-error">{{ error }}</p>
        <button class="button button--primary web-login-submit" type="submit" :disabled="pending">
          <LoaderCircle v-if="pending" class="spin" :size="16" />
          <LockKeyhole v-else :size="16" />
          {{ pending ? t('webLogin.authenticating') : t('webLogin.enter') }}
        </button>
      </form>
      <small>{{ t('webLogin.footnote') }}</small>
    </section>
  </main>
</template>

<style scoped>
.web-login-shell {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 32px;
}

.web-login-card {
  width: min(460px, 100%);
  padding: 34px;
  border: 1px solid var(--line-strong);
  border-radius: 18px;
  background: color-mix(in srgb, var(--panel) 94%, transparent);
  box-shadow: 0 24px 80px rgb(0 0 0 / 28%);
}

.web-login-mark {
  position: relative;
  width: 76px;
  height: 76px;
  display: grid;
  place-items: center;
  margin-bottom: 22px;
}

.web-login-mark span {
  position: absolute;
  inset: 0;
  border: 1px solid var(--accent);
  border-radius: 50%;
  opacity: 0.34;
}

.web-login-mark span:nth-child(2) {
  inset: 10px;
  opacity: 0.58;
}

h1 {
  margin: 8px 0 10px;
  font-size: clamp(28px, 5vw, 40px);
}

p {
  color: var(--text-muted);
  line-height: 1.6;
}

form {
  display: grid;
  gap: 16px;
  margin-top: 28px;
}

label > span {
  display: block;
  margin-bottom: 7px;
  color: var(--text-muted);
  font-size: 11px;
  letter-spacing: 0.12em;
}

label > div {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 13px;
  border: 1px solid var(--line-strong);
  border-radius: 10px;
  background: var(--panel-strong);
}

input {
  width: 100%;
  min-height: 44px;
  border: 0;
  outline: 0;
  color: var(--text);
  background: transparent;
  font: inherit;
}

.web-login-submit {
  justify-content: center;
  min-height: 44px;
}

.form-error {
  margin: -4px 0 0;
  color: var(--critical);
}

small {
  display: block;
  margin-top: 18px;
  color: var(--text-subtle);
  line-height: 1.5;
}
</style>
